import { useState } from 'preact/hooks';
import type { PrActionBody, PrDetailBody } from '../../shared/pr/api';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiSend } from '../lib/api';

// Procurement changes the cost centre a request is charged to, with a reason, until a PO draft
// exists (D105). Shown on the review panel and the request page; the broker checks again.

const ERRORS = new Set(['reason_required', 'invalid_transition', 'conflict', 'forbidden']);

export function ChangeVertical({
  pr,
  onDone,
}: {
  pr: PrDetailBody;
  onDone: (message: string) => void;
}) {
  const { t } = useI18n();
  const [vertical, setVertical] = useState(pr.vertical);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  if (!pr.canChangeVertical) return null;

  async function save() {
    if (!reason.trim()) return setProblem(t('review.error.reason_required'));
    setBusy(true);
    setProblem(null);
    try {
      await apiSend<PrActionBody>('POST', `/api/prs/${encodeURIComponent(pr.prNo)}/action`, {
        action: 'changeVertical',
        etag: pr.etag,
        vertical,
        reason,
      });
      onDone(t('vertical.done', { no: pr.prNo }));
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      const code = e?.code ?? 'generic';
      setProblem(
        t((ERRORS.has(code) ? `review.error.${code}` : 'review.error.generic') as MessageKey) +
          (e?.correlationId ? ` ${t('error.reference', { ref: e.correlationId })}` : ''),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <details class="review-office-change">
      <summary>{t('vertical.change')}</summary>
      <p class="caption">{t('vertical.hint')}</p>
      {problem && (
        <p class="form-problem" role="alert">
          {problem}
        </p>
      )}
      <div class="admin-form__grid">
        <div class="field">
          <label for={`vertical-${pr.prNo}`}>{t('vertical.label')}</label>
          <select
            id={`vertical-${pr.prNo}`}
            value={vertical}
            onChange={(e) => setVertical((e.target as HTMLSelectElement).value)}
          >
            {!pr.verticals.includes(pr.vertical) && (
              <option value={pr.vertical}>{pr.vertical}</option>
            )}
            {pr.verticals.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div class="field">
          <label for={`vertical-reason-${pr.prNo}`}>{t('vertical.reason')}</label>
          <input
            id={`vertical-reason-${pr.prNo}`}
            type="text"
            maxLength={2000}
            value={reason}
            onInput={(e) => setReason((e.target as HTMLInputElement).value)}
          />
        </div>
      </div>
      <button
        type="button"
        class="button"
        disabled={busy || vertical === pr.vertical}
        onClick={() => void save()}
      >
        {t('vertical.save')}
      </button>
    </details>
  );
}
